import {
  addError,
  arrayMax,
  dateOrder,
  isBlank,
  maxLength,
  numberRange,
  oneOf,
  optionalDate,
  optionalUrl,
  requiredString,
  result,
} from './common';

const movieStatuses = ['Want to watch', 'Watching', 'Watched', 'Paused', 'Abandoned'];
const showStatuses = ['Returning', 'Ended', 'Canceled', 'In Production', 'Unknown'];
const showUserStatuses = ['Want to watch', 'Watching', 'Watched', 'Paused', 'Abandoned'];
const bookShelves = ['Want to read', 'Reading', 'Read', 'Paused', 'Abandoned'];
const bookFormats = ['Physical', 'Ebook', 'Audiobook', 'Mixed'];
const progressTypes = ['Pages', 'Percent', 'Minutes'];
const tripStatuses = ['Idea', 'Planning', 'Booked', 'In progress', 'Completed', 'Canceled'];
const tripTypes = ['Solo', 'Couple', 'Family', 'Friends', 'Business'];
const intensities = ['Easy', 'Moderate', 'Hard', 'Max effort'];
const workoutModes = ['structured', 'session'];
const workoutTypes = ['Strength', 'Yoga', 'Pilates', 'Bodyweight', 'Class', 'Walk', 'Run', 'Cycling', 'Swim', 'Sport', 'Cardio', 'Mobility', 'Other'];
const workoutStatuses = ['Planned', 'Completed', 'Skipped'];
const hikeStatuses = ['Want to hike', 'Planned', 'Completed', 'Skipped'];
const hikeDifficulties = ['Easy', 'Moderate', 'Hard', 'Strenuous'];
const trailTypes = ['Loop', 'Out and back', 'Point to point'];
const trailTerrains = ['', 'Paved', 'Dirt', 'Rocky', 'Snow', 'Mixed'];
const crowdLevels = ['', 'Quiet', 'Moderate', 'Busy'];
const distanceUnits = ['mi', 'km'];
const elevationUnits = ['ft', 'm'];
const recipeDifficulties = ['Easy', 'Medium', 'Advanced'];
const recipeStatuses = ['Want to try', 'Tried', 'Regular', 'Retired'];
const recipeMealTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Dessert', 'Meal Prep'];
const recipeUnits = ['', 'tsp', 'tbsp', 'cup', 'fl oz', 'oz', 'lb', 'g', 'kg', 'ml', 'l', 'pinch', 'dash', 'clove', 'can', 'package', 'piece', 'slice', 'bunch', 'sprig', 'custom'];
const recipeQuantityPattern = /^(\d+(\.\d+)?|\d+\/\d+|\d+\s+\d+\/\d+)$/;
const unicodeFractions = {
  '¼': '1/4',
  '½': '1/2',
  '¾': '3/4',
  '⅐': '1/7',
  '⅑': '1/9',
  '⅒': '1/10',
  '⅓': '1/3',
  '⅔': '2/3',
  '⅕': '1/5',
  '⅖': '2/5',
  '⅗': '3/5',
  '⅘': '4/5',
  '⅙': '1/6',
  '⅚': '5/6',
  '⅛': '1/8',
  '⅜': '3/8',
  '⅝': '5/8',
  '⅞': '7/8',
};

/**
 * Normalizes recipe quantity.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
export function normalizeRecipeQuantity(value) {
  return String(value || '')
    .trim()
    .replace(/(\d)([¼½¾⅐⅑⅒⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])/g, '$1 $2')
    .replace(/[¼½¾⅐⅑⅒⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]/g, (fraction) => unicodeFractions[fraction] || fraction)
    .replace(/\s+/g, ' ');
}

/**
 * Validates recipe form.
 *
 * @param {object} form - form value.
 * @returns {*} The derived operation result.
 */
export function validateRecipeForm(form = {}) {
  const errors = {};
  requiredString(errors, 'title', form.title, 'Recipe name');
  requiredString(errors, 'recipeBook', form.recipeBook, 'Recipe book');
  maxLength(errors, 'title', form.title, 180, 'Recipe name');
  maxLength(errors, 'recipeBook', form.recipeBook, 120, 'Recipe book');
  if (!Array.isArray(form.ingredients) || !form.ingredients.some((ingredient) => !isBlank(ingredient.item))) {
    addError(errors, 'ingredients', 'Add at least one ingredient.');
  }
  if (!Array.isArray(form.instructions) || !form.instructions.some((step) => !isBlank(step.text))) {
    addError(errors, 'instructions', 'Add at least one instruction.');
  }
  optionalUrl(errors, 'sourceUrl', form.sourceUrl, 'Source URL');
  oneOf(errors, 'difficulty', form.difficulty, recipeDifficulties, 'Difficulty');
  oneOf(errors, 'status', form.status, recipeStatuses, 'Status');
  numberRange(errors, 'rating', form.rating, { min: 0.5, max: 5, label: 'Rating' });
  numberRange(errors, 'prepTime', form.prepTime, { min: 0, max: 10080, label: 'Prep time' });
  numberRange(errors, 'cookTime', form.cookTime, { min: 0, max: 10080, label: 'Cook time' });
  numberRange(errors, 'totalTime', form.totalTime, { min: 0, max: 10080, label: 'Total time' });
  numberRange(errors, 'servings', form.servings, { min: 0, max: 1000, label: 'Servings' });
  arrayMax(errors, 'ingredients', form.ingredients, 200, 'Ingredients');
  arrayMax(errors, 'instructions', form.instructions, 100, 'Instructions');
  arrayMax(errors, 'categories', form.categories, 30, 'Categories');
  arrayMax(errors, 'mealTypes', form.mealTypes, 10, 'Meal types');
  (form.mealTypes || []).forEach((mealType, index) => {
    oneOf(errors, `mealTypes.${index}`, mealType, recipeMealTypes, 'Meal type');
  });
  (form.ingredients || []).forEach((ingredient, index) => {
    const quantity = normalizeRecipeQuantity(ingredient?.quantity);
    if (!isBlank(quantity) && !recipeQuantityPattern.test(String(quantity).trim())) {
      addError(errors, `ingredients.${index}.quantity`, 'Quantity can use numbers or fractions, like 1, 1.5, 1/2, or 1 1/2.');
    }
    oneOf(errors, `ingredients.${index}.unit`, ingredient?.unit, recipeUnits, 'Unit');
  });
  return result(errors);
}

/**
 * Validates movie form.
 *
 * @param {object} form - form value.
 * @returns {*} The derived operation result.
 */
export function validateMovieForm(form = {}) {
  const errors = {};
  requiredString(errors, 'title', form.title, 'Title');
  maxLength(errors, 'title', form.title, 180, 'Title');
  oneOf(errors, 'status', form.status, movieStatuses, 'Status');
  numberRange(errors, 'rating', form.rating, { min: 0.5, max: 5, label: 'Rating' });
  numberRange(errors, 'releaseYear', form.releaseYear, { min: 1888, max: 2100, label: 'Release year' });
  optionalUrl(errors, 'posterUrl', form.posterUrl, 'Poster image URL');
  optionalDate(errors, 'watchedDate', form.watchedDate, 'Watched date');
  return result(errors);
}

/**
 * Validates tvshow form.
 *
 * @param {object} form - form value.
 * @returns {*} The derived operation result.
 */
export function validateTVShowForm(form = {}) {
  const errors = {};
  requiredString(errors, 'title', form.title, 'Title');
  maxLength(errors, 'title', form.title, 180, 'Title');
  oneOf(errors, 'status', form.status, showStatuses, 'Show status');
  oneOf(errors, 'userStatus', form.userStatus, showUserStatuses, 'User status');
  numberRange(errors, 'rating', form.rating, { min: 0.5, max: 5, label: 'Rating' });
  numberRange(errors, 'firstAirYear', form.firstAirYear, { min: 1900, max: 2100, label: 'First air year' });
  optionalUrl(errors, 'posterUrl', form.posterUrl, 'Poster image URL');
  return result(errors);
}

/**
 * Validates reading form.
 *
 * @param {object} form - form value.
 * @returns {*} The derived operation result.
 */
export function validateReadingForm(form = {}) {
  const errors = {};
  requiredString(errors, 'title', form.title, 'Title');
  maxLength(errors, 'title', form.title, 180, 'Title');
  oneOf(errors, 'shelf', form.shelf, bookShelves, 'Shelf');
  oneOf(errors, 'format', form.format, bookFormats, 'Format');
  oneOf(errors, 'progressType', form.progressType, progressTypes, 'Progress type');
  numberRange(errors, 'rating', form.rating, { min: 0.5, max: 5, label: 'Rating' });
  numberRange(errors, 'publishedYear', form.publishedYear, { min: 0, max: 2100, label: 'Published year' });
  numberRange(errors, 'pageCount', form.pageCount, { min: 0, max: 100000, label: 'Page count' });
  numberRange(errors, 'currentPage', form.currentPage, { min: 0, max: 100000, label: 'Current page' });
  numberRange(errors, 'currentPercent', form.currentPercent, { min: 0, max: 100, label: 'Current percent' });
  numberRange(errors, 'currentMinutes', form.currentMinutes, { min: 0, max: 1000000, label: 'Minutes listened' });
  optionalUrl(errors, 'coverUrl', form.coverUrl, 'Cover image URL');
  optionalDate(errors, 'startedDate', form.startedDate, 'Started date');
  optionalDate(errors, 'finishedDate', form.finishedDate, 'Finished date');
  dateOrder(errors, form.startedDate, 'finishedDate', form.finishedDate, 'Finished date must be after started date.');
  return result(errors);
}

/**
 * Validates traveling form.
 *
 * @param {object} form - form value.
 * @returns {*} The derived operation result.
 */
export function validateTravelingForm(form = {}) {
  const errors = {};
  requiredString(errors, 'title', form.title, 'Trip title');
  requiredString(errors, 'destination', form.destination, 'Destination');
  maxLength(errors, 'title', form.title, 180, 'Trip title');
  oneOf(errors, 'status', form.status, tripStatuses, 'Status');
  oneOf(errors, 'tripType', form.tripType, tripTypes, 'Trip type');
  optionalDate(errors, 'startDate', form.startDate, 'Start date');
  optionalDate(errors, 'endDate', form.endDate, 'End date');
  dateOrder(errors, form.startDate, 'endDate', form.endDate, 'End date must be after start date.');
  return result(errors);
}

/**
 * Validates fitness form.
 *
 * @param {object} form - form value.
 * @returns {*} The derived operation result.
 */
export function validateFitnessForm(form = {}) {
  const errors = {};
  requiredString(errors, 'title', form.title, 'Title');
  maxLength(errors, 'title', form.title, 180, 'Title');
  oneOf(errors, 'mode', form.mode, workoutModes, 'Mode');
  oneOf(errors, 'workoutType', form.workoutType, workoutTypes, 'Workout type');
  oneOf(errors, 'status', form.status, workoutStatuses, 'Status');
  optionalDate(errors, 'workoutDate', form.workoutDate, 'Workout date');
  optionalDate(errors, 'scheduledAt', form.scheduledAt, 'Planned for');
  optionalDate(errors, 'completedAt', form.completedAt, 'Completed on');
  optionalDate(errors, 'plannedDate', form.plannedDate, 'Planned date');
  optionalDate(errors, 'completedDate', form.completedDate, 'Completed date');
  oneOf(errors, 'intensity', form.intensity, intensities, 'Intensity');
  oneOf(errors, 'distanceUnit', form.distanceUnit, distanceUnits, 'Distance unit');
  numberRange(errors, 'rating', form.rating, { min: 0.5, max: 5, label: 'Rating' });
  numberRange(errors, 'durationMinutes', form.durationMinutes, { min: 0, max: 1440, label: 'Duration' });
  numberRange(errors, 'caloriesBurned', form.caloriesBurned, { min: 0, max: 100000, label: 'Calories' });
  numberRange(errors, 'averageHeartRate', form.averageHeartRate, { min: 0, max: 250, label: 'Average heart rate' });
  numberRange(errors, 'maxHeartRate', form.maxHeartRate, { min: 0, max: 250, label: 'Max heart rate' });
  numberRange(errors, 'elevationGain', form.elevationGain, { min: 0, max: 100000, label: 'Elevation gain' });
  arrayMax(errors, 'strengthExercises', form.strengthExercises, 100, 'Exercises');
  (form.strengthExercises || []).forEach((exercise, exerciseIndex) => {
    maxLength(errors, `strengthExercises.${exerciseIndex}.name`, exercise?.name, 180, 'Exercise name');
    arrayMax(errors, `strengthExercises.${exerciseIndex}.sets`, exercise?.sets, 50, 'Exercise sets');
    (exercise?.sets || []).forEach((set, setIndex) => {
      numberRange(errors, `strengthExercises.${exerciseIndex}.sets.${setIndex}.reps`, set?.reps, { min: 0, max: 10000, label: 'Reps' });
      numberRange(errors, `strengthExercises.${exerciseIndex}.sets.${setIndex}.durationSeconds`, set?.durationSeconds, { min: 0, max: 86400, label: 'Set duration' });
      numberRange(errors, `strengthExercises.${exerciseIndex}.sets.${setIndex}.restSeconds`, set?.restSeconds, { min: 0, max: 86400, label: 'Rest time' });
    });
  });
  return result(errors);
}

/**
 * Validates hiking form.
 *
 * @param {object} form - form value.
 * @returns {*} The derived operation result.
 */
export function validateHikingForm(form = {}) {
  const errors = {};
  requiredString(errors, 'title', form.title, 'Trail name');
  maxLength(errors, 'title', form.title, 180, 'Trail name');
  maxLength(errors, 'location', form.location, 180, 'Location');
  optionalDate(errors, 'hikeDate', form.hikeDate, 'Hike date');
  oneOf(errors, 'status', form.status, hikeStatuses, 'Status');
  oneOf(errors, 'difficulty', form.difficulty, hikeDifficulties, 'Difficulty');
  oneOf(errors, 'trailType', form.trailType, trailTypes, 'Trail type');
  oneOf(errors, 'terrain', form.terrain, trailTerrains, 'Terrain');
  oneOf(errors, 'crowdLevel', form.crowdLevel, crowdLevels, 'Crowd level');
  oneOf(errors, 'distanceUnit', form.distanceUnit, distanceUnits, 'Distance unit');
  oneOf(errors, 'elevationUnit', form.elevationUnit, elevationUnits, 'Elevation unit');
  numberRange(errors, 'latitude', form.latitude, { min: -90, max: 90, label: 'Latitude' });
  numberRange(errors, 'longitude', form.longitude, { min: -180, max: 180, label: 'Longitude' });
  optionalUrl(errors, 'routeUrl', form.routeUrl, 'Route URL');
  optionalUrl(errors, 'mapUrl', form.mapUrl, 'Map URL');
  maxLength(errors, 'geometrySummary', form.geometrySummary, 240, 'Geometry summary');
  numberRange(errors, 'elevationGain', form.elevationGain, { min: 0, max: 100000, label: 'Elevation gain' });
  numberRange(errors, 'durationMinutes', form.durationMinutes, { min: 0, max: 10080, label: 'Duration' });
  numberRange(errors, 'actualDurationMinutes', form.actualDurationMinutes, { min: 0, max: 10080, label: 'Actual duration' });
  numberRange(errors, 'rating', form.rating, { min: 1, max: 5, label: 'Rating' });
  arrayMax(errors, 'checklist', form.checklist, 100, 'Checklist');
  return result(errors);
}
